import React, { memo, useRef, useState, useEffect } from "react";
import { COLORS } from "../../pages/ManualSalary/constants";

interface UploadFieldProps {
  file?: File;
  placeholder: string;
  loading?: boolean;
  onChange: (file?: File) => void;
}

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB
const ALLOWED_TYPE = "application/pdf";

const UploadField: React.FC<UploadFieldProps> = ({
  file,
  placeholder,
  loading = false,
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState("");
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const triggerFileSelect = () => {
    if (!loading) {
      // If file exists, show PDF viewer instead of file picker
      if (file) {
        setShowPdfViewer(true);
      } else {
        inputRef.current?.click();
      }
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.type !== ALLOWED_TYPE) {
      setError("Only PDF files are allowed");
      e.target.value = "";
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      setError("File size must be less than 2 MB");
      e.target.value = "";
      return;
    }

    setError("");
    onChange(selectedFile);
  };

  const removeFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setError("");
    onChange(undefined);
    if (inputRef.current) inputRef.current.value = "";
  };

  const closePdfViewer = () => {
    setShowPdfViewer(false);
  };

  const handleDownload = () => {
    if (!pdfUrl || !file) return;

    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Create object URL from File when file changes
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPdfUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setPdfUrl(null);
    }
  }, [file]);

  // Handle body scroll lock when PDF viewer is open
  useEffect(() => {
    if (showPdfViewer) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [showPdfViewer]);

  return (
    <div className="d-flex flex-column">
      {/* CARD */}
      <div
        onClick={triggerFileSelect}
        style={{
          border: "1px solid #d0d5dd",
          borderRadius: 16,
          padding: "0 16px",
          height: 50,
          display: "flex",
          alignItems: "center",
          gap: 12,
          cursor: loading ? "not-allowed" : "pointer",
          background: "#fff",
          minWidth: 260,
        }}
      >
        {loading ? (
          <>
            {/* INLINE LOADER */}
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            />
            <span style={{ color: COLORS.TEXT_MUTED }}>Uploading…</span>
          </>
        ) : (
          <>
            {/* FILE ICON */}
            <i
              className="bi bi-file-earmark-text"
              style={{ fontSize: 22, flexShrink: 0 }}
            />

            {/* FILE NAME / PLACEHOLDER */}
            <span
              style={{
                flex: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: file ? "#000" : COLORS.TEXT_MUTED,
                lineHeight: "22px",
              }}
            >
              {file ? file.name : placeholder}
            </span>

            {/* DELETE ICON (PERFECTLY CENTERED) */}
            {file && (
              <button
                type="button"
                onClick={removeFile}
                title="Remove file"
                style={{
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  border: "none",
                  background: "transparent",
                  color: "#d92d20",
                  lineHeight: 1,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <i
                  className="bi bi-trash"
                  style={{
                    fontSize: 18,
                    display: "block",
                  }}
                />
              </button>
            )}
          </>
        )}
      </div>

      {/* HIDDEN INPUT */}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        hidden
        onChange={onFileChange}
      />

      {/* ERROR */}
      {error && (
        <span className="text-danger mt-1" style={{ fontSize: 12 }}>
          {error}
        </span>
      )}

      {/* PDF VIEWER MODAL */}
      {showPdfViewer && pdfUrl && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center"
          style={{
            background: "rgba(0,0,0,0.4)",
            zIndex: 9999,
            padding: "40px 0",
          }}
          onClick={closePdfViewer}
        >
          <div
            className="pdf-modal-content d-flex flex-column bg-white p-3 rounded shadow"
            style={{
              width: "90%",
              maxWidth: "900px",
              height: "100%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 style={{ color: COLORS.TEXT_PRIMARY }}>{""}</h5>
              <button className="btn p-2" onClick={closePdfViewer}>
                <i className="bi bi-x-lg" />
              </button>
            </div>

            {/* PDF VIEWER AREA */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                width: "100%",
                background: "white",
              }}
            >
              <iframe
                src={pdfUrl}
                title="PDF preview"
                style={{ width: "100%", height: "100%", border: 0 }}
              />
            </div>

            {/* FOOTER */}
            <div className="d-flex border-top align-items-center justify-content-center w-100 pt-3">
              <button
                type="button"
                className="btn border d-flex align-items-center gap-2 px-4 py-2"
                onClick={handleDownload}
                style={{ fontWeight: 500 }}
              >
                <i className="bi bi-cloud-download" />
                <span>Download PDF</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(UploadField);
